import { ContentCopy } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { AuthPermissions, BillingAddress, PaylinkResponse, currencyUtils } from 'skipify-types';

import { formatCurrency } from '../../../../util/formatters';
import { getDisplayedPaylinkUserInfo } from '../../../../util/helpers';
import { PaylinkStatusChip } from '../StatusChip/PaylinkStatusChip';
import { TransactionStatusChip } from '../StatusChip/TransactionStatusChip';
import { SkeletonRows } from '../../../../common/DataTable/SkeletonRows';
import { useMerchant } from '../../../../contexts/MerchantContext';
import { DialogCloseButton } from '../../../../components/core/DialogCloseButton';
import { useGetPaylinkQuery } from '../../../../store/modules/paylink/api';
import { PaylinkTransaction, TransactionStatus } from '../../../../store/modules/orders/types';
import { PaymentMethodCell } from '../../../../common/Customers/PaymentMethodCell';

const formatBillingAddress = (address: BillingAddress | null) => {
  if (!address) return '';
  const result = ['address1', 'address2', 'address3', 'city', 'state']
    .map((key) => address[key as keyof BillingAddress])
    .filter((s) => s)
    .join(', ');

  return address.zipCode
    ? `${result} ${address.zipCode}, ${address.country}`
    : `${result}, ${address.country}`;
};
const transformPaylinkTransactions = (
  paylink: PaylinkResponse | undefined,
): PaylinkTransaction[] => {
  if (!paylink || !paylink.transactions) return [];

  return paylink.transactions.map((transaction) => {
    return {
      merchantId: paylink.merchantId,
      paylinkId: paylink.paylinkId,
      transactionId: transaction.skipifyTransactionId,
      transactionTimestamp: transaction.transactionTimestamp,
      transactionType: transaction.transactionType,
      transactionStatus: transaction.transactionStatus as TransactionStatus,
      billingAddress: formatBillingAddress(transaction.billingAddress),
      transactionAmount: transaction.transactionAmount,
      orderId: paylink.orderId,
      nameOnCard: null, // Not available in PaylinkSuccessfulTransaction
      last4: transaction.cardLastFour,
      cardBrand: transaction.cardBrand,
      pspTransactionId: transaction.pspTransactionId,
      paymentMethodType: transaction.paymentMethodType,
      bankName: transaction.bankName,
      accountType: transaction.accountType,
      accountName: transaction.accountName,
    };
  });
};

const PaylinkRelatedInfo = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <Stack spacing={1}>
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 600,
        color: (theme) => theme.palette.grey[700],
        whiteSpace: 'nowrap',
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        '& p': {
          fontSize: '14px',
          lineHeight: '18px',
          whiteSpace: 'nowrap',
          maxWidth: { xs: '130px', sm: '200px', md: '180px' },
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
      }}
    >
      {children}
    </Box>
  </Stack>
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  paylink: PaylinkResponse;
  merchantName: string;
}

export const TransactionInfoDialog = ({ isOpen, onClose, paylink, merchantName }: Props) => {
  const { canAccess } = useMerchant();
  const { data: paylinkResponse, isLoading: isLoadingTransactions } = useGetPaylinkQuery(
    { paylinkId: paylink.paylinkId },
    {
      skip:
        !isOpen ||
        !(
          canAccess(AuthPermissions.READ_PAYLINK_TRANSACTIONS) ||
          canAccess(AuthPermissions.READ_OWN_PAYLINK_TRANSACTIONS)
        ),
    },
  );

  const transformedTransactions = React.useMemo(() => {
    return transformPaylinkTransactions(paylinkResponse);
  }, [paylinkResponse]);

  const isFlexPay = !!(paylinkResponse?.isFlexPay ?? paylink.isFlexPay);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>Transaction Info</DialogTitle>
      <DialogCloseButton onClose={onClose} />
      <DialogContent dividers>
        <Stack spacing={4}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: (theme) => theme.palette.grey[700],
            }}
          >
            The following transactions were made for the Paylink:
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr' },
              columnGap: { xs: 2, sm: 4, md: 8 },
              rowGap: 2,
            }}
          >
            <PaylinkRelatedInfo title="Paylink Status">
              <PaylinkStatusChip status={paylink.status} size="small" />
            </PaylinkRelatedInfo>
            <PaylinkRelatedInfo title="Customer">
              <Typography>{getDisplayedPaylinkUserInfo(paylink).userInfo}</Typography>
            </PaylinkRelatedInfo>
            <PaylinkRelatedInfo title="Merchant Name">
              <Typography>{merchantName}</Typography>
            </PaylinkRelatedInfo>
            <PaylinkRelatedInfo title="Amount">
              <Typography>
                {formatCurrency(
                  currencyUtils.minorToMajor(paylink.orderTotal ?? paylink.total),
                  paylink.currencyCode,
                )}
              </Typography>
            </PaylinkRelatedInfo>
            <PaylinkRelatedInfo title="Description">
              <Typography>{paylink.description || 'N/A'}</Typography>
            </PaylinkRelatedInfo>
          </Box>
          <TableContainer component={Box} sx={{ position: 'relative', minHeight: '120px' }}>
            <Table
              sx={{
                '& th': {
                  fontWeight: 600,
                  fontSize: '12px',
                  border: 0,
                },
                '& tr': {
                  height: '50px',
                },
                '& td': {
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                },
              }}
              aria-label="Transaction List"
            >
              <TableHead
                sx={{
                  position: 'sticky',
                  top: 0,
                  background: (theme) => theme.palette.grey[100],
                  padding: 2.5,
                  zIndex: 1,
                }}
              >
                <TableRow>
                  <TableCell component="th">Transaction ID</TableCell>
                  <TableCell component="th">Billing Address</TableCell>
                  <TableCell component="th">Payment</TableCell>
                  <TableCell component="th" align="right">
                    Amount
                  </TableCell>
                  <TableCell component="th">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoadingTransactions ? (
                  <SkeletonRows pageSize={1} rowHeight={45} gap={10} marginTop={45} />
                ) : (
                  transformedTransactions.map(
                    ({
                      pspTransactionId,
                      billingAddress,
                      transactionAmount,
                      transactionStatus,
                      transactionId,
                      last4,
                      cardBrand,
                      paymentMethodType,
                      bankName,
                      accountType,
                    }) => (
                      <TableRow key={transactionId}>
                        <TableCell scope="row">
                          <Stack direction="row" alignItems="center">
                            <Typography sx={{ fontSize: '13px' }}>{pspTransactionId}</Typography>
                            <IconButton
                              aria-label="copy"
                              size="small"
                              sx={{ my: -1 }}
                              onClick={() => navigator.clipboard.writeText(pspTransactionId ?? '')}
                            >
                              <ContentCopy
                                sx={{ fontSize: '16px', color: (theme) => theme.palette.grey[700] }}
                              />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell>{billingAddress}</TableCell>
                        <TableCell>
                          <PaymentMethodCell
                            paymentMethod={{
                              networkType: cardBrand ?? '',
                              lastFour: last4,
                              paymentMethodType,
                              bankName,
                              accountType,
                            }}
                            isFlexPay={isFlexPay}
                            variant="compact"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            currencyUtils.minorToMajor(transactionAmount ?? 0),
                            paylink.currencyCode,
                          )}
                        </TableCell>
                        <TableCell>
                          <TransactionStatusChip status={transactionStatus} />
                        </TableCell>
                      </TableRow>
                    ),
                  )
                )}
                {!isLoadingTransactions && transformedTransactions.length === 0 && (
                  <Box component="tr">
                    <Box
                      component="td"
                      sx={{
                        display: 'block',
                        position: 'absolute',
                        top: '70px',
                        width: '100%',
                        textAlign: 'center',
                        color: (theme) => theme.palette.grey[700],
                        fontStyle: 'italic',
                      }}
                    >
                      No transactions found
                    </Box>
                  </Box>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
